// Calendar arithmetic: Julian day numbers, Julian Pascha, tone, period of the moveable cycle.
// A date carries its own calendar: Julian (stil vechi) or, with `nou`, the revised calendar of the Romanian
// Church since 1924 (fixed feasts on new style, Pascha and the whole moveable cycle on the old Paschalion).

import type { CivilDate, Pascha, PaschaContext, Tone, YMD } from "./types.ts";

export function intre(offset: number | null | undefined, a: number, b: number): boolean {
  return offset !== null && offset !== undefined && offset >= a && offset <= b;
}

export function jdnIulian(an: number, luna: number, zi: number): number {
  const a = Math.floor((14 - luna) / 12);
  const y = an + 4800 - a;
  const m = luna + 12 * a - 3;
  return zi + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - 32083;
}

export function jdnGregorian(an: number, luna: number, zi: number): number {
  const a = Math.floor((14 - luna) / 12);
  const y = an + 4800 - a;
  const m = luna + 12 * a - 3;
  return zi + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4)
    - Math.floor(y / 100) + Math.floor(y / 400) - 32045;
}

export function dinJdnIulian(jdn: number): YMD {
  const j = jdn + 32082;
  const b = Math.floor((4 * j + 3) / 1461);
  const c = j - Math.floor((1461 * b) / 4);
  const d = Math.floor((5 * c + 2) / 153);
  const zi = c - Math.floor((153 * d + 2) / 5) + 1;
  const luna = d + 3 - 12 * Math.floor(d / 10);
  const an = b - 4800 + Math.floor(d / 10);
  return { an, luna, zi };
}

export function dinJdnGregorian(jdn: number): YMD {
  const a = jdn + 32044;
  const b = Math.floor((4 * a + 3) / 146097);
  const c = a - Math.floor((146097 * b) / 4);
  const d = Math.floor((4 * c + 3) / 1461);
  const e = c - Math.floor((1461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  const zi = e - Math.floor((153 * m + 2) / 5) + 1;
  const luna = m + 3 - 12 * Math.floor(m / 10);
  const an = b * 100 + d - 4800 + Math.floor(m / 10);
  return { an, luna, zi };
}

/** 0 = Sunday. */
export function ziSaptamana(jdn: number): number {
  return (jdn + 1) % 7;
}

const ZILE_LUNA = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export function zileInLuna(an: number, luna: number, nou?: boolean): number {
  if (luna === 2) {
    const bisect = nou ? (an % 4 === 0 && an % 100 !== 0) || an % 400 === 0 : an % 4 === 0;
    return bisect ? 29 : 28;
  }
  return ZILE_LUNA[luna] ?? 0;
}

/** Absolute day of a date: gives the weekday and the distance from Pascha; month and day give the Menaion. */
export function jdnDe(date: CivilDate): number {
  return date.nou ? jdnGregorian(date.an, date.luna, date.zi) : jdnIulian(date.an, date.luna, date.zi);
}

/** A fixed feast (month, day) of year `an`, in the calendar of `date`. */
export function jdnFix(date: CivilDate, an: number, luna: number, zi: number): number {
  return date.nou ? jdnGregorian(an, luna, zi) : jdnIulian(an, luna, zi);
}

export function iulianDinGregorian(an: number, luna: number, zi: number): YMD {
  return dinJdnIulian(jdnGregorian(an, luna, zi));
}

export function gregorianDinIulian(an: number, luna: number, zi: number): YMD {
  return dinJdnGregorian(jdnIulian(an, luna, zi));
}

/** Julian Pascha. On the old style there are no exceptions: d never exceeds 28, so Pascha falls 22 March–25 April. */
export function paschaIulian(an: number): Pascha {
  const a = an % 19;
  const b = an % 4;
  const c = an % 7;
  const d = (19 * a + 15) % 30;
  const e = (2 * b + 4 * c + 6 * d + 6) % 7;
  const martie = d + e < 10;
  return { an, luna: martie ? 3 : 4, zi: martie ? 22 + d + e : d + e - 9, a, b, c, d, e, mutat: "" };
}

export function jdnPasti(an: number): number {
  const p = paschaIulian(an);
  return jdnIulian(p.an, p.luna, p.zi);
}

export function contextPasti(date: CivilDate): PaschaContext {
  const j = jdnDe(date);
  for (let i = -1; i <= 1; i++) {
    const an = date.an + i;
    const p = jdnPasti(an);
    const off = j - p;
    if (off >= -70 && off <= 56) return { an, jdn: p, offset: off, jdnZi: j };
  }
  const an = j < jdnPasti(date.an) + 7 ? date.an - 1 : date.an;
  return { an, jdn: jdnPasti(an), offset: null, jdnZi: j };
}

export function wdDate(date: CivilDate): number {
  return ziSaptamana(jdnDe(date));
}

const GLAS_LUMINATA = [1, 2, 3, 4, 5, 6, 8];

export function glas(date: CivilDate): Tone {
  const j = jdnDe(date);
  for (let i = -1; i <= 1; i++) {
    const p = jdnPasti(date.an + i);
    if (j >= p && j <= p + 6) {
      const g = GLAS_LUMINATA[j - p] ?? null;
      return {
        numar: g,
        text: "Glasul " + g + ". În Săptămâna Luminată glasul se schimbă în fiecare zi (1, 2, 3, 4, 5, 6, 8; glasul 7 se lasă), cu stihirile Învierii, nu cu Octoihul de rând.",
      };
    }
    if (j >= p - 8 && j < p) {
      return { numar: null, text: "Nu se cântă Octoihul de la Sâmbăta lui Lazăr până la Paști." };
    }
  }
  let thomas = -Infinity;
  for (let i = -1; i <= 1; i++) {
    const t = jdnPasti(date.an + i) + 7;
    if (t <= j && t > thomas) thomas = t;
  }
  const numar = (Math.floor((j - thomas) / 7) % 8) + 1;
  return {
    numar,
    text: "Glasul " + numar + ". De la Duminica Tomei, glasul 1, câte o săptămână, până la Paștile următoare.",
  };
}

export function saptamanaPost(off: number): number | null {
  if (off >= -48 && off <= -42) return 1;
  if (off >= -41 && off <= -35) return 2;
  if (off >= -34 && off <= -28) return 3;
  if (off >= -27 && off <= -21) return 4;
  if (off >= -20 && off <= -14) return 5;
  if (off >= -13 && off <= -8) return 6;
  return null;
}

export function perioada(offset: number | null): string {
  if (offset === null) return "Perioada Octoihului.";
  if (offset === -70) return "Duminica Vameșului și a Fariseului.";
  if (intre(offset, -69, -64)) return "Săptămâna Vameșului și a Fariseului.";
  if (offset === -63) return "Duminica Fiului risipitor.";
  if (intre(offset, -62, -57)) return "Săptămâna Fiului risipitor.";
  if (offset === -56) return "Duminica Lăsatului sec de carne.";
  if (intre(offset, -55, -50)) return "Săptămâna brânzei.";
  if (offset === -49) return "Duminica Lăsatului sec de brânză.";
  if (intre(offset, -48, -8)) return "Sfântul și Marele Post, săptămâna " + saptamanaPost(offset) + ".";
  if (offset === -7) return "Duminica Floriilor.";
  if (intre(offset, -6, -1)) return "Săptămâna Patimilor.";
  if (offset === 0) return "Sfânta Înviere.";
  if (intre(offset, 1, 6)) return "Săptămâna Luminată.";
  if (offset === 7) return "Duminica Tomei.";
  if (offset === 14) return "Duminica Mironosițelor.";
  if (offset === 21) return "Duminica Slăbănogului.";
  if (offset === 24) return "Înjumătățirea Cincizecimii.";
  if (offset === 28) return "Duminica Samarinencei.";
  if (offset === 35) return "Duminica Orbului.";
  if (offset === 38) return "Odovania Paștelui.";
  if (offset === 39) return "Înălțarea Domnului.";
  if (offset === 42) return "Duminica Sfinților Părinți de la întâiul sinod.";
  if (offset === 47) return "Odovania Înălțării.";
  if (offset === 48) return "Sâmbăta morților, pomenirea celor adormiți.";
  if (offset === 49) return "Duminica Cincizecimii.";
  if (intre(offset, 50, 55)) return "Săptămâna Cincizecimii.";
  if (offset === 56) return "Duminica Tuturor Sfinților.";
  if (intre(offset, 8, 48)) return "Perioada Penticostarului.";
  return "Perioada Octoihului.";
}
